import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { CalendarIcon } from 'lucide-react';
import { fetchAllRows } from '@/lib/supabaseHelpers';

const DAY_NAMES = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];

const LiveHourHeatmap = () => {
  const [pageviews, setPageviews] = useState<{ created_at: string }[]>([]);
  const [completions, setCompletions] = useState<{ updated_at: string }[]>([]);

  const fetchData = async () => {
    const [pvData, compData] = await Promise.all([
      fetchAllRows<{ created_at: string }>('quiz_pageviews', 'created_at', [
        { column: 'utm_source', operator: 'not.is', value: null },
      ]),
      fetchAllRows<{ updated_at: string }>('quiz_sessions', 'updated_at', [
        { column: 'is_completed', operator: 'eq', value: true },
        { column: 'utm_source', operator: 'not.is', value: null },
      ]),
    ]);
    setPageviews(pvData);
    setCompletions(compData);
  };

  useEffect(() => {
    fetchData();
    const channel = supabase
      .channel('live-heatmap')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_pageviews' }, () => fetchData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_sessions' }, () => fetchData())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  // Build hour distribution (0-23)
  const hourCounts: number[] = new Array(24).fill(0);
  const hourCompletions: number[] = new Array(24).fill(0);
  
  pageviews.forEach(pv => {
    const h = new Date(pv.created_at).getHours();
    hourCounts[h]++;
  });

  completions.forEach(c => {
    const h = new Date(c.updated_at).getHours();
    hourCompletions[h]++;
  });

  const maxCount = Math.max(...hourCounts, 1);

  // Day of week distribution
  const dayCounts: number[] = new Array(7).fill(0);
  const dayCompletions: number[] = new Array(7).fill(0);
  
  pageviews.forEach(pv => {
    const d = new Date(pv.created_at).getDay();
    dayCounts[d]++;
  });

  completions.forEach(c => {
    const d = new Date(c.updated_at).getDay();
    dayCompletions[d]++;
  });

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
        <CalendarIcon className="w-4 h-4" />
        Dag & Uur Heatmap
        <span className="ml-auto flex items-center gap-1.5 text-xs font-medium text-emerald-600">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          LIVE
        </span>
      </h3>

      {/* Hour heatmap */}
      <p className="text-xs text-gray-500 font-medium mb-2">Pageviews per uur</p>
      <div className="flex gap-0.5 mb-4">
        {hourCounts.map((count, hour) => {
          const intensity = maxCount > 0 ? count / maxCount : 0;
          const cvr = count > 0 ? Math.round((hourCompletions[hour] / count) * 100) : 0;
          return (
            <div
              key={hour}
              className="flex-1 rounded-sm relative group cursor-default"
              style={{
                height: 32,
                backgroundColor: count === 0
                  ? '#f3f4f6'
                  : `rgba(37, 99, 235, ${0.15 + intensity * 0.85})`,
              }}
              title={`${hour}:00 — ${count} pv, ${hourCompletions[hour]} voltooid (${cvr}% CVR)`}
            >
              <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-[8px] text-gray-400">
                {hour % 3 === 0 ? `${hour}` : ''}
              </span>
            </div>
          );
        })}
      </div>

      {/* Day of week */}
      <p className="text-xs text-gray-500 font-medium mb-2 mt-6">Per dag van de week</p>
      <div className="grid grid-cols-7 gap-2">
        {DAY_NAMES.map((day, i) => {
          const cvr = dayCounts[i] > 0 ? Math.round((dayCompletions[i] / dayCounts[i]) * 100) : 0;
          return (
            <div key={day} className="text-center">
              <p className="text-[10px] text-gray-400 mb-1">{day}</p>
              <p className="text-sm font-bold text-gray-900">{dayCounts[i]}</p>
              <p className="text-[10px] text-gray-400">{dayCounts[i] > 0 ? `${cvr}%` : '—'}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LiveHourHeatmap;
